import * as React from "react";
import * as toastr from "toastr";

import { convertDateStructToDate } from "../../classes/data";
import { IEndsOn, IRecurrence, IWeeklyRecurrence, RecurrenceType } from "../../classes/recurrencePattern";
import { IText } from "../../classes/text";
import { IText_AddEditEvent } from "../../classes/text_AddEditEvent";

import { getInitialDailyRecurrencePattern, getInitialEndsOnPattern, getInitialMonthlyRecurrencePattern, getInitialRecurrenceType, getInitialWeeklyRecurrencePattern, getInitialYearlyRecurrencePattern } from "./RecurrencePatternEditor_InitialState";

import { RecurrencePatternEnd } from "./EndRecurrence/RecurrencePatternEnd";
import { RecurrencePatternRepeatFrequencyDropdown } from "./RecurrencePatternRepeatFrequencyDropdown";
import { RecurrencePatternSaveAndCancelButtons } from "./RecurrencePatternSaveAndCancelButtons";
import { RecurrencePatternWeekly } from "./WeeklyRecurrence/RecurrencePatternWeekly";

import * as dates from "../../../calendar/reducers/changeTimePeriod";

export interface IRecurrencePatternEditor {
	addEditText: IText_AddEditEvent;
	currentStartDate: Date;
	dateOfFirstEventInRecurrence: Date;
	firstDayOfWeek: string;
	isFromTwoWayCalendar: boolean;
	isTwoWayCalendarEvent: boolean;
	onCloseModal: () => any;
	onUpdateRecurrence: (recurrence: IRecurrence, dateOfFirstEventInRecurrence: Date, firstDayOfWeek: string) => void;
	text: IText;
	recurrencePattern: IRecurrence;
	userDateFormat: string;
}

export const RecurrencePatternEditor: React.SFC<IRecurrencePatternEditor> = (props) => {

	// State hooks:
	const [selectedRecurrenceType, setSelectedRecurrenceTypeInState] = React.useState(getInitialRecurrenceType(props));
	const [selectedDailyRecurrencePattern, setSelectedDailyRecurrencePatternInState] = React.useState(getInitialDailyRecurrencePattern(props));
	const [selectedWeeklyRecurrencePattern, setSelectedWeeklyRecurrencePatternInState] = React.useState(getInitialWeeklyRecurrencePattern(props));
	const [selectedMonthlyRecurrencePattern, setSelectedMonthlyRecurrencePatternInState] = React.useState(getInitialMonthlyRecurrencePattern(props));
	const [selectedYearlyRecurrencePattern, setSelectedYearlyRecurrencePatternInState] = React.useState(getInitialYearlyRecurrencePattern(props));
	const [selectedEndsOnPattern, setSelectedEndsOnPatternInState] = React.useState(getInitialEndsOnPattern(props));

	const [saveHasBeenClicked, setSaveHasBeenClickedInState] = React.useState(false);

	// State update methods:
	const updateWeeklyRecurrenceStateProperty = (statePropertyName: string, statePropertyValue: any): void => {
		const s = Object.assign({}, selectedWeeklyRecurrencePattern) as IWeeklyRecurrence;
		s.endsOn = selectedEndsOnPattern;
		s[statePropertyName] = statePropertyValue;
		setSelectedWeeklyRecurrencePatternInState(s);
	};

	const updateEndsOnStateProperty = (statePropertyValue: any): void => {
		const s = Object.assign({}, statePropertyValue) as IEndsOn;
		setSelectedEndsOnPatternInState(s);
		updateWeeklyRecurrenceStateProperty("endsOn", statePropertyValue);
	};

	const onSave = () => {
		setSaveHasBeenClickedInState(true);

		if (getIfAllValidationsArePassed()) {
			// Call service to update the text describing the recurrence pattern for Weekly recurrence. TODO: Daily/Monthly/Yearly recurrence patterns.
			switch (selectedRecurrenceType) {
				case "Daily": {
					toastr.error("Updating the recurrence description for DAILY recurrences when updating the recurrence has not yet been implemented.");
					break;
				}
				case "Weekly": {
					props.onUpdateRecurrence(selectedWeeklyRecurrencePattern, props.dateOfFirstEventInRecurrence, props.firstDayOfWeek);
					break;
				}
				case "Monthly": {
					toastr.error("Updating the recurrence description for MONTHLY recurrences when updating the recurrence has not yet been implemented.");
					break;
				}
				case "Yearly": {
					toastr.error("Updating the recurrence description for YEARLY recurrences when updating the recurrence has not yet been implemented.");
					break;
				}
				default: {
					toastr.error("Unknown recurrence frequency type: " + selectedRecurrenceType);
				}
			}
		}

	};

	// Validation:
	const getIfAtLeastOneDayWasTicked = (): boolean => {
		if (selectedRecurrenceType === "Weekly") {
			return selectedWeeklyRecurrencePattern.repeatsOnWeekDays.length > 0;
		}
		return true;
	};

	const getIfEndsOnDateIsSet = (): boolean => {
		if (selectedEndsOnPattern.endsOnType === "endsOnDate") {
			return selectedEndsOnPattern.hasOwnProperty("endsOnDate");
		}
		return true;
	};

	const getIfEndDateIsOnOrAfterStartDate = (): boolean => {
		if (selectedEndsOnPattern.endsOnType === "endsOnDate") {
			if (selectedEndsOnPattern.hasOwnProperty("endsOnDate")) {
				return props.currentStartDate && dates.isSameDayOrAfter(convertDateStructToDate(selectedEndsOnPattern.endsOnDate), props.currentStartDate);
			}
		}
		return true;
	};

	const getIfAllValidationsArePassed = () => {
		const atLeastOneDayWasTicked = getIfAtLeastOneDayWasTicked();
		const endsOnDateIsSet = getIfEndsOnDateIsSet();
		const endDateIsNotAfterStartDate = getIfEndDateIsOnOrAfterStartDate();

		return atLeastOneDayWasTicked && endsOnDateIsSet && endDateIsNotAfterStartDate;
	};

	// Display data:
	const tempAlertStyle = { backgroundColor: "lemonchiffon", padding: "10px", borderWidth: "2px", borderStyle: "dashed", borderRadius: "10px", borderColor: "gold", fontFamily: "Comic Sans MS", fontSize: "10px", opacity: 0.5, marginBottom: "5px", marginTop: "5px" };
	const isTwoWayCalendar = props.isFromTwoWayCalendar || props.isTwoWayCalendarEvent;
	const txtRecurrence = props.text.eventViews.recurrence;

	return (
		<div className="CMS_element">

			<div className="CMS_title CMS_T4"><em className="fa fa-refresh"></em> {txtRecurrence}</div>

			<div className="CMS_e_row">
				<RecurrencePatternRepeatFrequencyDropdown
					isTwoWayCalendar={isTwoWayCalendar}
					onChangeSelectedRepeatFrequency={(value) => (setSelectedRecurrenceTypeInState(value as RecurrenceType))}
					repeatFreqency={selectedRecurrenceType}
					text={props.text}
				/>
			</div>

			<div className="CMS_e_row">
				<div className="CMS_break"></div>
			</div>

			{selectedRecurrenceType !== "Weekly" &&
				<div style={tempAlertStyle}>The <b>{selectedRecurrenceType}</b> Event Pattern UI will be displayed here, once that's developed in a future ticket.</div>
			}

			{selectedRecurrenceType === "Daily" &&
				<div style={tempAlertStyle}><pre>{JSON.stringify(selectedDailyRecurrencePattern, null, 2)}</pre></div>
			}

			{selectedRecurrenceType === "Weekly" &&
				<RecurrencePatternWeekly
					firstDayOfWeek={props.firstDayOfWeek}
					isTwoWayCalendar={isTwoWayCalendar}
					onChangeRepeatsEveryXWeeks={(value) => updateWeeklyRecurrenceStateProperty("repeatsEvery", value)}
					onChangeRepeatsOnWeekdays={(value) => updateWeeklyRecurrenceStateProperty("repeatsOnWeekDays", value)}
					repeatEveryXWeeks={selectedWeeklyRecurrencePattern.repeatsEvery}
					repeatsOnWeekDays={selectedWeeklyRecurrencePattern.repeatsOnWeekDays}
					saveHasBeenClicked={saveHasBeenClicked}
					text={props.text}
					validationAtLeastOneDayIsTicked={getIfAtLeastOneDayWasTicked()}
				/>
			}

			{selectedRecurrenceType === "Monthly" &&
				<div style={tempAlertStyle}><pre>{JSON.stringify(selectedMonthlyRecurrencePattern, null, 2)}</pre></div>
			}

			{selectedRecurrenceType === "Yearly" &&
				<div style={tempAlertStyle}><pre>{JSON.stringify(selectedYearlyRecurrencePattern, null, 2)}</pre></div>
			}

			<RecurrencePatternEnd
				addEditText={props.addEditText}
				endsOn={selectedEndsOnPattern}
				firstDayOfWeek={props.firstDayOfWeek}
				onChangeEndsOn={(value) => updateEndsOnStateProperty(value)}
				saveHasBeenClicked={saveHasBeenClicked}
				startDate={props.currentStartDate}
				text={props.text}
				userDateFormat={props.userDateFormat}
				validationEndDateIsEqualToOrAfterStartDate={getIfEndDateIsOnOrAfterStartDate()}
				validationEndDateIsSet={getIfEndsOnDateIsSet()}
			/>

			{/* <div style={tempAlertStyle}><pre>{JSON.stringify(selectedWeeklyRecurrencePattern, null, 2)}</pre></div> */}

			<RecurrencePatternSaveAndCancelButtons
				allValidationIsPassed={getIfAllValidationsArePassed()}
				onCloseModal={props.onCloseModal}
				onSave={onSave}
				saveHasBeenClicked={saveHasBeenClicked}
				text={props.text}
			/>

		</div>
	);
};
